import type { Row, RowData, TableFeatures } from "@tanstack/react-table";
import type { DataGridDisplayTextMeta } from "../cells/cellValues";
import { toSearchQuery } from "../cells/highlightMatches";
import { getRowSearchText } from "../cells/rowSearchText";
import { resolveColumnMeta } from "../columns/columnKinds";
import { getColumnDefId, isDisplayColumnKind } from "../columns/columnLayout";
import type { DataGridColumn } from "../columns/types";

/** The value columns search matches, by id, with the meta that shapes their text. */
type SearchColumns = ReadonlyMap<string, DataGridDisplayTextMeta>;

/** What `useTable` needs for search over one column array. */
export interface DataGridSearch {
    /** The one column TanStack filters on; it answers for the whole row. */
    primaryId: string | undefined;
    filter: ReturnType<typeof createSearchFilter>;
}

const searchByColumns = new WeakMap<object, DataGridSearch>();

/** A column without an accessor has no value, and TanStack can't filter on it. */
const hasAccessor = <TData extends RowData>(def: DataGridColumn<TData>) =>
    ("accessorFn" in def && def.accessorFn !== undefined) ||
    ("accessorKey" in def && def.accessorKey !== undefined);

const collectSearchColumns = <TData extends RowData>(
    columns: ReadonlyArray<DataGridColumn<TData>>,
): SearchColumns => {
    const metaById = new Map<string, DataGridDisplayTextMeta>();
    const visit = (defs: ReadonlyArray<DataGridColumn<TData>>) => {
        for (const def of defs) {
            if ("columns" in def && def.columns) {
                visit(def.columns);
                continue;
            }
            const id = getColumnDefId(def);
            if (id === undefined) {
                continue;
            }
            const meta = resolveColumnMeta(def, id);
            if (
                !isDisplayColumnKind(meta.kind) &&
                def.enableGlobalFilter !== false &&
                hasAccessor(def)
            ) {
                metaById.set(id, meta);
            }
        }
    };
    visit(columns);
    return metaById;
};

/**
 * The global filter: a case-insensitive substring match on each row's search
 * text (every searchable column as its cell shows it, so dates match "Jun 30"
 * and entities match by name). TanStack calls a global filter once per row
 * per filterable column, so only `primaryId` is filterable and it answers for
 * the whole row: one `includes` per row. The query is normalized once per pass.
 */
const createSearchFilter = (searchColumns: SearchColumns) =>
    Object.assign(
        <TFeatures extends TableFeatures, TData extends RowData>(
            row: Row<TFeatures, TData>,
            _columnId: string,
            needle: string,
        ): boolean =>
            needle === "" ||
            getRowSearchText(row, searchColumns).includes(needle),
        {
            resolveFilterValue: <TValue>(query: TValue) =>
                toSearchQuery(query).toLowerCase(),
        },
    );

/**
 * Search for a column array, built once per array identity so the per-row
 * search text cache (keyed on the searchable columns) survives data changes.
 */
export const getDataTableSearch = <TData extends RowData>(
    columns: ReadonlyArray<DataGridColumn<TData>>,
): DataGridSearch => {
    const cached = searchByColumns.get(columns);
    if (cached) {
        return cached;
    }
    const searchColumns = collectSearchColumns(columns);
    const search = {
        primaryId: searchColumns.keys().next().value,
        filter: createSearchFilter(searchColumns),
    };
    searchByColumns.set(columns, search);
    return search;
};
